import { FormEvent, ChangeEvent, useEffect, useState } from "react";
import { useSimpleForm } from "../../hooks/useSimpleForm";
import CustomTextInput from "../../shared/CustomInput/CustomTextInput";
import CustomPasswordInput from "../../shared/CustomPasswordInput/CustomPasswordInput";
import CustomButton from "../../shared/CustomButton/CustomButton";
import CustomCheckBox from "../../shared/CustomCheckBox/CustomCheckBox";
import "./Login.scss";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { useToastAlert } from "../../hooks/useToastAlert/useToastAlert";
import OnBoardingLogin from "../../components/Login/OnBoardingLogin/OnBoardingLogin";
import { useCustomNavigate } from "../../hooks/useCustomNavigate";
import LeopartOnDarkvector from "../../assets/icons/LeopartOnDarkvector";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";
import { useTransition } from "../../providers/TransitionProvider";
import GoogleSignInButton from "../../shared/GoogleSignInButton/GoogleSignInButton";

const REMEMBERED_EMAIL_KEY = "leopart_remembered_email";

interface LoginForm {
  email: string;
  password: string;
}

const AUTH_BASE = (() => {
  const BASE_URL = process.env.REACT_APP_BASE_URL || '';
  return BASE_URL.endsWith('/api')
    ? BASE_URL.replace(/\/api$/, '') + '/api/auth'
    : BASE_URL + '/auth';
})();

interface ResetPasswordModalProps {
  tempToken: string;
  onSuccess: () => void;
}

const ResetPasswordModal = ({ tempToken, onSuccess }: ResetPasswordModalProps) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const toastAlert = useToastAlert();

  const handleReset = async (e?: FormEvent) => {
    e?.preventDefault();
    if (newPassword !== confirmPassword) {
      toastAlert({ duration: 4000, type: "error", message: "Le password non coincidono" });
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${AUTH_BASE}/reset-password`, {
        token: tempToken,
        newPassword,
      });
      toastAlert({ duration: 4000, type: "success", message: "Password aggiornata con successo" });
      onSuccess();
    } catch {
      toastAlert({ duration: 4000, type: "error", message: "Errore durante il reset della password" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-password-overlay">
      <form className="reset-password-card" onSubmit={handleReset}>
        <div className="reset-password-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h2 className="text-bold-20 reset-password-title">Cambia password</h2>
        <p className="text-regular-14 reset-password-desc">
          Devi impostare una nuova password prima di continuare.
        </p>
        <div className="reset-password-fields">
          <CustomPasswordInput
            label="Nuova password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <CustomPasswordInput
            label="Conferma password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>
        <CustomButton fullWidth isLoading={isLoading} type="submit">
          Aggiorna password
        </CustomButton>
      </form>
    </div>
  );
};

export const Login = () => {
  const { size, getClassesNameByQuery } = useWindowDimensions();
  const { navigate } = useCustomNavigate();
  const toastAlert = useToastAlert();
  const [isLoadingLogin, setisLoadingLogin] = useState(false);
  const [onBoarding, setOnBoarding] = useState(false);
  const [mustResetPassword, setMustResetPassword] = useState(false);
  const [tempToken, setTempToken] = useState<string>("");
  /**
   * Errore dell'accesso con Google, mostrato SOTTO il bottone e non in un
   * toast: un toast dura pochi secondi, e qui il messaggio dice cosa fare
   * ("chiedi a chi gestisce la società di inserirti") — va letto con calma.
   */
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(REMEMBERED_EMAIL_KEY));
  const { loginWithCredentials, loginWithGoogle, keycloak } = useAuth();
  const { playTransition } = useTransition();

  const { form, handleChange, handleEventInputChange } = useSimpleForm<LoginForm>({
    password: "",
    email: "",
  });

  useEffect(() => {
    keycloak.authenticated && navigate("GYM_DASHBOARD");
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (savedEmail) {
      handleChange("email", savedEmail);
    }
    // eslint-disable-next-line
  }, []);

  const handleLogin = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!form.email || !form.password) return;
    setisLoadingLogin(true);
    try {
      const result = await loginWithCredentials(form.email, form.password);
      if (result?.mustResetPassword) {
        setTempToken(result.tempToken);
        setMustResetPassword(true);
        return;
      }
      if (rememberMe) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, form.email);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
      playTransition(() => navigate("GYM_DASHBOARD"));
    } catch (error) {
      toastAlert({
        duration: 4000,
        type: "error",
        message: "Autenticazione fallita",
      });
    } finally {
      setisLoadingLogin(false);
    }
  };

  /**
   * Accesso con Google.
   *
   * L'`idToken` si inoltra e basta: la sua firma la verifica il server, che è
   * l'unico a poterlo fare. Da qui in poi il percorso è quello del login con
   * password — stessi token, stessa destinazione.
   */
  const handleGoogleLogin = async (idToken: string) => {
    setisLoadingLogin(true);
    setGoogleError(null);
    try {
      await loginWithGoogle(idToken);
      playTransition(() => navigate("GYM_DASHBOARD"));
    } catch (error: any) {
      // Il server distingue "non ti conosco" da "token non valido": sono due
      // cose diverse per chi le legge, la prima si risolve chiedendo alla
      // società, la seconda riprovando. Se la chiamata non è nemmeno partita
      // non c'è `response`, e allora si mostra il messaggio di rete.
      setGoogleError(
        error?.response?.data?.message ??
          error?.message ??
          "Accesso con Google fallito",
      );
    } finally {
      setisLoadingLogin(false);
    }
  };

  const renderFooter = () => {
    return (
      <div className="footer text-medium-14">
        <span
          onClick={() => {
            setOnBoarding((prev) => !prev);
          }}
        >
          © 2024 Leopart. All Rights Reserved.
        </span>
        <div className="container__right">
          <span className="pe-4">Privacy Policy</span>
          <span>Termini e condizioni</span>
        </div>
      </div>
    );
  };
  const renderHeader = () => {
    return (
      <div className="header text-medium-14">
        <div className="d-flex align-items-center">
          <LeopartOnDarkvector w={"44px"} h={"44px"} />
          <span className="title">Leopart</span>
        </div>
        <div>
          <div className="beta__button text-bold-14">
            {size.width >= 576 ? "Entra nella Beta Coach" : "Beta Coach"}
          </div>
        </div>
      </div>
    );
  };

  const renderLoginForm = () => {
    return (
      /*
        La card è un <div> e il <form> sta DENTRO, con i soli campi.
        È la forma che usano già ConfirmUser, ForgotPassword e le altre: Login
        era l'unica pagina in cui `container__form` era un <form>. Serve perché
        `container__form` è posizionata in assoluto al centro — mettendo il
        bottone di Google come suo fratello, finiva in cima alla pagina, fuori
        dalla card.
      */
      <div id="container-form" className="container__form">
        <h1
          className={`title ${getClassesNameByQuery({
            xs: {
              "text-bold-24": true,
            },
            sm: {
              "text-bold-32": true,
            },
          })}`}
        >
          Accedi
        </h1>
        <span
          className={`description ${getClassesNameByQuery({
            xs: {
              "text-regular-14": true,
            },
            sm: {
              "text-regular-16": true,
            },
          })}`}
        >
          Direttori / Preparatori Atletici
        </span>
        <form className="login__fields" onSubmit={handleLogin}>
        <CustomTextInput
          label="Email"
          value={form.email}
          className="email__input"
          type="email"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            handleEventInputChange("email", event)
          }
        />
        <CustomPasswordInput
          label="Password"
          value={form.password}
          className="password__input"
          onChange={(event) => handleEventInputChange("password", event)}
        />
        <div className="remember__row">
          <div className="remember__check">
            <CustomCheckBox
              value={rememberMe}
              onChange={(val) => setRememberMe(val)}
            />
            <span className="text-regular-14" onClick={() => setRememberMe(!rememberMe)}>Ricordami</span>
          </div>
          <Link to="/forgot-password">
            <span className="text-bold-14 forgot__password__link">Password dimenticata?</span>
          </Link>
        </div>
        <CustomButton
          fullWidth
          className="submit__button"
          isLoading={isLoadingLogin}
          type="submit"
          disabled={!form.email || !form.password}
        >
          Accedi
        </CustomButton>
        </form>

        {/*
          Fuori dal <form> ma DENTRO la card. Il bottone di Google non invia
          nulla: parla con Google e poi chiama la nostra API. Stando dentro
          l'elemento <form>, un clic poteva farne partire l'invio — la pagina si
          ricaricava e il messaggio d'errore spariva prima di poterlo leggere.
        */}
        <div className="login__divider">
        <span className="text-regular-12">oppure</span>
      </div>
        <GoogleSignInButton
          onCredential={handleGoogleLogin}
          errorMessage={googleError}
        />
      </div>
    );
  };
  return (
    <div className="container__login">
      {mustResetPassword && (
        <ResetPasswordModal
          tempToken={tempToken}
          onSuccess={() => {
            setMustResetPassword(false);
            playTransition(() => navigate("GYM_DASHBOARD"));
          }}
        />
      )}
      <div className="container__header">
        {renderHeader()}
        {onBoarding ? (
          <OnBoardingLogin
            steps={[
              {
                answer: "Quanto pesi?",
                placeholder: "inserisci il tuo peso",
                type: "number",
                rightAddon: "kg",
                key: "key",
              },
              {
                answer: "Quanto sei alto?",
                placeholder: "inserisci il tuo peso",
                type: "number",
                rightAddon: "cm",
                key: "key-2",
              },
              {
                answer: "Quanto sei coglione?",
                placeholder: "inserisci il tuo peso",
                rightAddon: "kg",
                type: "string",
                key: "key-3",
              },
            ]}
          />
        ) : (
          renderLoginForm()
        )}
      </div>
      <div className="container__footer">{renderFooter()}</div>
    </div>
  );
};

export default Login;
